import React, { useEffect, useState } from 'react';
import { IndianRupee, ShoppingBag, TrendingUp, Truck, Loader2, Package } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import api from '@/lib/api';
import { formatINR } from '@/lib/format';

export default function AdminOverview() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get('/admin/stats').then(({ data }) => setStats(data)).catch(() => {}).finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="py-24 grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-[var(--brand-blue)]" /></div>;
    if (!stats) return <p>Could not load stats.</p>;

    const cards = [
        { label: 'Revenue', value: formatINR(stats.revenue), icon: IndianRupee, tint: 'bg-blue-50 text-blue-600' },
        { label: 'Paid orders', value: stats.total_orders, icon: ShoppingBag, tint: 'bg-green-50 text-green-600' },
        { label: 'Avg. order value', value: formatINR(stats.aov), icon: TrendingUp, tint: 'bg-amber-50 text-amber-600' },
        { label: 'Pending shipments', value: stats.pending_shipments, icon: Truck, tint: 'bg-purple-50 text-purple-600' },
    ];

    return (
        <div>
            <h1 className="font-display text-2xl lg:text-3xl font-bold">Overview</h1>
            <p className="text-[var(--ink-muted)] mt-1">Your store at a glance.</p>

            <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
                {cards.map((c) => (
                    <div key={c.label} data-testid={`stat-${c.label.toLowerCase().replace(/[^a-z]+/g, '-')}`} className="rounded-2xl border border-border bg-white p-5">
                        <div className={`h-10 w-10 rounded-xl grid place-items-center ${c.tint}`}><c.icon className="h-5 w-5" /></div>
                        <p className="mt-3 text-sm text-[var(--ink-muted)]">{c.label}</p>
                        <p className="font-display text-2xl font-bold mt-0.5">{c.value}</p>
                    </div>
                ))}
            </div>

            <div className="mt-6 grid lg:grid-cols-[1.6fr_1fr] gap-6">
                <div className="rounded-2xl border border-border bg-white p-5">
                    <h2 className="font-display font-semibold">Sales over time</h2>
                    <div className="h-64 mt-4">
                        {stats.sales_series.length === 0 ? (
                            <div className="h-full grid place-items-center text-sm text-[var(--ink-muted)]">No sales data yet</div>
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={stats.sales_series} margin={{ left: -10, right: 10, top: 10 }}>
                                    <defs><linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2A44E8" stopOpacity={0.35} /><stop offset="100%" stopColor="#2A44E8" stopOpacity={0} /></linearGradient></defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
                                    <XAxis dataKey="date" tickFormatter={(d) => (d || '').slice(5)} fontSize={12} stroke="#94a3b8" />
                                    <YAxis fontSize={12} stroke="#94a3b8" />
                                    <Tooltip formatter={(v) => formatINR(v)} />
                                    <Area type="monotone" dataKey="revenue" stroke="#2A44E8" strokeWidth={2.5} fill="url(#rev)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>

                <div className="rounded-2xl border border-border bg-white p-5">
                    <h2 className="font-display font-semibold flex items-center gap-2"><Package className="h-4 w-4" /> Top products</h2>
                    <div className="mt-4 space-y-3">
                        {stats.top_products.length === 0 ? (
                            <p className="text-sm text-[var(--ink-muted)]">No sales yet.</p>
                        ) : stats.top_products.map((p, i) => (
                            <div key={i} className="flex items-center gap-3">
                                <span className="h-7 w-7 rounded-full bg-[var(--surface-2)] grid place-items-center text-sm font-bold">{i + 1}</span>
                                <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{p.name}</p><p className="text-xs text-[var(--ink-muted)]">{p.qty} sold</p></div>
                                <span className="font-semibold text-sm">{formatINR(p.revenue)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
